import React, { useContext } from 'react'
import CardXClient from "./CardXClient";
import { deepCopy } from '../../Components/Shared/Utility/DeepCopy';
import Payment from './Payment';
import { getDateWithOffset } from '../../Components/Shared/Utility/GetDateWithOffset';
import AbpnSpinner from '../../Components/Shared/Components/AbpnSpinner';
import { IPaymentCharge } from './PaymentChargeModel';
import { PaymentType } from './PaymentType';
import { appInsights } from '../AppInsights';
import { ProfileContext } from '../../Components/Auth/ProfileProvider';

interface ICardXCreditCardProps {
    onPaymentChanged: (payment: Payment) => void;
    showPaymentButton : boolean;
    isDisabled : boolean;
    payment: Payment,
}

interface ICardXResponse { 
        credDebType: string;
        pi_avs_response : string;
        pi_cvv_response : string;
        pd_collect_shipping_information : string;
        pd_display_items : string;
        pi_response_code : string;
        pi_response_status : string;
        pt_ach_account_type : string;
        pt_authorization_code : string;
        pt_card_expiration : string;
        pt_card_number : string;
        pt_card_token : string;
        pt_card_type : string;
        pt_currency : string;
        pt_custom_name_1 : string;
        pt_custom_value_1 : string;
        pt_decline_order_id : string;
        pt_gateway_account : string;
        pt_order_id : string;
        pt_surcharge_amount : number;
        pt_transaction_amount : number;
        pt_payment_session : string;
        pt_account_code_1 : string;
        pt_account_code_2 : string;
        pt_account_code_3 : string;
        pt_billing_address_1 : string;
        pt_billing_city : string;
        pt_billing_email_address : string;
        pt_billing_postal_code : string;
        pt_billing_state : string;
        pt_payment_name : string;
        pt_billing_country : string;
        processor_message : string;
        isFraudDetectRun : boolean;
        country : string;
        response : string;
}

let didCardXInitialize : boolean = false;

export const CardXCreditCard = React.forwardRef((props : ICardXCreditCardProps) => {
    const { user } = useContext(ProfileContext);
    
    var cardXRef = React.createRef<HTMLDivElement>();

    const updatePayment = (cardXResponse : ICardXResponse, paymentCharge : IPaymentCharge) => {
        const paymentClone = deepCopy(props.payment);

        const cardXPayment : Partial<Payment> = {
            city: cardXResponse.pt_billing_city,
            state: cardXResponse.pt_billing_state,        
            method: "card",
            email: cardXResponse.pt_billing_email_address,
            LastFour: cardXResponse.pt_card_number.substring(-4),
            PaymentToken: cardXResponse.pt_card_token,            
            IsValid: paymentCharge.isSuccessful,
            paymentCharge: paymentCharge,
            paymentType: paymentCharge.paymentType,
            hasBeenCharged: true
        };

        const updatedPayment : Payment = {...paymentClone, ...cardXPayment};
        props.onPaymentChanged(updatedPayment);
    }

    const handlePaymentError = (err : Error) => {
        const paymentClone = deepCopy(props.payment);
        const updatedPayment : Payment = {...paymentClone, 
            error: `${err}`
        };

        props.onPaymentChanged(updatedPayment);
    }

    React.useEffect(() => {
        if (didCardXInitialize) return;

        function getCardXPaymentType(cardType : string) : PaymentType {
            if (cardType == "amex") return PaymentType.AmericanExpress;

            return PaymentType.CreditCardVisa;
        }

        async function onCardXCallback(err : Error, cardXResponse : ICardXResponse) {
            if (err != null) {
                await appInsights.logError(err);
                handlePaymentError(err);
                return;
            }

            if (cardXResponse.response === 'payment cancelled') {
                var paymentCancelledError = new Error(`Payment Cancelled`);
                await appInsights.logError(paymentCancelledError);
                handlePaymentError(paymentCancelledError);
                return;
            }

            const isPaymentSuccess = cardXResponse.pi_response_status == 'success';

            if (!isPaymentSuccess) {
                var paymentUnsuccessfulError = new Error(`Unsuccessful payment: ${JSON.stringify(cardXResponse)}`);
                await appInsights.logError(paymentUnsuccessfulError);
            }

            const paymentCharge : IPaymentCharge = {
                paymentProcessorAccount: cardXResponse.pt_gateway_account,
                cardHolderName: cardXResponse.pt_payment_name,
                cardNumber: cardXResponse.pt_card_number,
                paymentType: getCardXPaymentType(cardXResponse.pt_card_type.toLowerCase()),
                response: JSON.stringify(cardXResponse),
                transactionDate: new Date(),
                responseCode: cardXResponse.pi_response_code,
                transactionId: cardXResponse.pt_order_id,
                surchargeAmount: cardXResponse.pt_surcharge_amount,
                transactionAmount: cardXResponse.pt_transaction_amount,
                isSuccessful: isPaymentSuccess
            };

            updatePayment(cardXResponse, paymentCharge);
        }

        async function initializeCardX() {
            await CardXClient.initializeCardX({
                amount: `${props.payment.amount}`,
                cardElement: cardXRef.current,
                mode: 'payment',
                includeBillingFields: true,
                isBillingRequired: true,
                callbackFn: onCardXCallback,
                accountNumber: user.physicianId
            });
        }

        initializeCardX();
        didCardXInitialize = true;

        return () => {
            CardXClient.disposeCardX();
            didCardXInitialize = false;
        }

    }, []);

    React.useEffect(() => {
        CardXClient.updateScriptAttributes({
            amount: `${props.payment.amount}`,
        });

    }, [props.payment]);

    React.useEffect(() => {
        CardXClient.updateScriptAttributes({accountNumber: user.physicianId});
    }, [user.physicianId]);

    const cardWrapperStyle = {
        'display': props.showPaymentButton ? 'block' : 'none',
        'marginTop': '20px'
    };

    return (
        <div style={cardWrapperStyle} className={props.isDisabled ? 'disabled' : ''}>
            <div id="pay_with_cardx" ref={cardXRef}><AbpnSpinner justifyContent='start'/></div>
        </div>
    );
});